/** Grouping a proposed package's changes — the ordering and the no-loss rule, on their own.
 *
 * Its own module for the reason `search.ts` and `register.ts` are: this is the part of REP-08's
 * screen that is a **decision rather than a layout**, and it is asserted directly by
 * `packagePreflight.test.tsx` rather than through rendered text. A copy change must not be able to break
 * the order; a reordering must fail a test rather than a reading.
 */

import type { PackageChange } from "../api/queries";

/** The kinds this screen has words for, in the order a reader needs them.
 *
 * **`recited` first and `added` last**, which is the opposite of how a diff is usually written and
 * is the point. A new line is visible in the report the next morning. A dropped check makes a
 * previously-held filing pass and nothing on any screen mentions it. A changed **citation** moves
 * what this product tells a customer the law says, with the sentence above it word for word the
 * same — which is why `_diff` reports it as its own kind rather than as a flavour of `amended`,
 * and why a screen that re-merged the two would undo the distinction in a template.
 *
 * Risk of going unread, descending.
 */
export const KIND_ORDER = ["recited", "removed", "amended", "added"] as const;

export type KnownKind = (typeof KIND_ORDER)[number];

/** Keyed on the **known** kinds rather than on `string`, so a lookup inside the loop over
 * `KIND_ORDER` is a value and not a maybe. A `Record<string, …>` would have made every heading
 * optional and pushed a `?? ""` into the render — a blank heading in exactly the place an
 * unrecognised kind is supposed to keep its own word. */
export const KINDS: Record<KnownKind, { heading: string; why: string }> = {
  recited: {
    heading: "The citation changed",
    why:
      "The requirement above each of these reads exactly as it did before — only the paragraph or " +
      "the URL behind it moved. This is the change a reviewer skims past, and it is the one that " +
      "moves what EmitIQ tells a customer the law says. Check each against the source before this " +
      "package is activated.",
  },
  removed: {
    heading: "No longer in the package",
    why:
      "A disclosure that stops being produced is a line a filing used to carry, and an omission " +
      "reads to a regulator as nothing being required. A dropped check is the change that makes a " +
      "previously-held filing pass, which is the direction nobody notices.",
  },
  amended: {
    heading: "Changed",
    why: "The line is still here and something about it is different. Each row names the fields.",
  },
  added: {
    heading: "New in the package",
    why: "Lines and checks this package has and the loaded one does not.",
  },
};

export interface ChangeGroup {
  kind: string;
  heading: string;
  /** Why this kind matters. `null` for a kind this screen has no words for — see `groupChanges`. */
  why: string | null;
  changes: readonly PackageChange[];
}

/** The changes, grouped by kind, in `KIND_ORDER`, **losing none of them**.
 *
 * `kind` is a plain string on the wire rather than an enum, so a fifth kind is a thing the server
 * can grow. A `switch` with four arms and no default would drop it silently, and an absence reads
 * as nothing having changed — design rule 5, in the one place on this screen where it would be
 * read as a reason to go ahead. So an unrecognised kind gets its own group, keeps its own word as
 * the heading, and says nothing about why it matters rather than guessing.
 */
export function groupChanges(changes: readonly PackageChange[]): ChangeGroup[] {
  const byKind = new Map<string, PackageChange[]>();
  for (const change of changes) {
    const list = byKind.get(change.kind);
    if (list) list.push(change);
    else byKind.set(change.kind, [change]);
  }

  const groups: ChangeGroup[] = [];
  for (const kind of KIND_ORDER) {
    const list = byKind.get(kind);
    if (list) {
      groups.push({ kind, heading: KINDS[kind].heading, why: KINDS[kind].why, changes: list });
      byKind.delete(kind);
    }
  }
  for (const [kind, list] of byKind) {
    groups.push({ kind, heading: kind, why: null, changes: list });
  }
  return groups;
}
